import React, { useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, CircularProgress } from '@material-ui/core';
import usePlanMapInit from '../hooks/usePlanMapInit';
import PlanEditToolbar from './PlanEditToolbar';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  map: {
    width: '100%',
    height: '100%',
    background: '#2d2d2d',
  },
  emptyState: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#2d2d2d',
    pointerEvents: 'none',
    zIndex: 500,
  },
  emptyInner: {
    textAlign: 'center',
    color: '#999',
    background: 'rgba(255,255,255,0.9)',
    padding: '16px 24px',
    borderRadius: 8,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 8, left: 12,
    background: 'rgba(255,255,255,0.9)',
    padding: '6px 12px',
    borderRadius: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    zIndex: 1000,
  },
  calibrationBanner: {
    position: 'absolute',
    top: 60,
    left: 12,
    background: 'rgba(255,152,0,0.9)',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 500,
    zIndex: 1100,
    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
  },
}));

const PlanMapContainer = ({
  mapRef,
  drawnItemsRef,
  imageUrl,
  imageWidth,
  imageHeight,
  loading,
  calibrated,
  calibrating,
  showMeasurements,
  onToggleMeasurements,
  onDrawPolygon,
  onDrawPolyline,
  onDrawPoint,
  onCalibrate,
  matching,
  activeSymbolUrl,
  activeItemName,
}) => {
  const classes = useStyles();
  const containerRef = useRef(null);

  usePlanMapInit(containerRef, {
    mapRef, drawnItemsRef,
    imageUrl, imageWidth, imageHeight,
  });

  return (
    <div className={classes.root}>
      <div ref={containerRef} className={classes.map} />

      {imageUrl && (
        <PlanEditToolbar
          onDrawPolygon={onDrawPolygon}
          onDrawPolyline={onDrawPolyline}
          onDrawPoint={onDrawPoint}
          onCalibrate={onCalibrate}
          calibrated={calibrated}
          showMeasurements={showMeasurements}
          onToggleMeasurements={onToggleMeasurements}
          matching={matching}
        />
      )}

      {!imageUrl && !loading && (
        <div className={classes.emptyState}>
          <div className={classes.emptyInner}>
            <Typography variant="body1" style={{ fontWeight: 500, marginBottom: 4 }}>
              No plan page selected
            </Typography>
            <Typography variant="caption">
              Select a page from the navigator to begin
            </Typography>
          </div>
        </div>
      )}

      {loading && (
        <div className={classes.loadingOverlay}>
          <CircularProgress size={14} />
          <span>Loading page image…</span>
        </div>
      )}

      {!calibrated && imageUrl && !calibrating && (
        <div className={classes.calibrationBanner}>
          Set the scale before drawing — click "Set Scale" above, then draw along a known dimension
        </div>
      )}

      {calibrating && (
        <div className={classes.calibrationBanner} style={{ background: 'rgba(21,101,192,0.95)' }}>
          Draw a line along a known distance on the plan (2 clicks)
        </div>
      )}

      {activeSymbolUrl && (
        <div style={{
          position: 'absolute',
          bottom: 12,
          left: 12,
          background: 'rgba(255,255,255,0.95)',
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          padding: 8,
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <img
            src={activeSymbolUrl}
            alt="Symbol"
            style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 4, border: '1px solid #e0e0e0' }}
          />
          <span style={{ fontSize: 11, color: '#555', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {activeItemName || 'Active item'}
          </span>
        </div>
      )}
    </div>
  );
};

export default PlanMapContainer;
